import { Directive, ElementRef, HostListener, Input, inject } from '@angular/core';

/**
 * Restricts input to numeric characters. Applied to number, tel, and numeric inputs.
 * Use `losAllowDecimal` on number fields that accept decimals (e.g. loan amount).
 * Use `losIntegerOnly` to force integers on type=number.
 */
@Directive({
  selector:
    'input[type=number]:not([losAllowText]), input[type=tel]:not([losAllowText]), input[inputmode=numeric]:not([losAllowText]), input[losNumericOnly]',
  standalone: true,
})
export class NumericOnlyInputDirective {
  private readonly element = inject(ElementRef<HTMLInputElement>);

  @Input() losAllowDecimal = false;
  @Input() losIntegerOnly = false;

  @HostListener('input')
  onInput(): void {
    this.sanitize();
  }

  @HostListener('paste')
  onPaste(): void {
    queueMicrotask(() => this.sanitize());
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (this.allowDecimal() && event.key === '.') {
      const value = this.element.nativeElement.value;
      if (!value.includes('.')) {
        return;
      }
    }

    if (/^\d$/.test(event.key)) {
      return;
    }

    event.preventDefault();
  }

  private allowDecimal(): boolean {
    const input = this.element.nativeElement;
    if (this.losIntegerOnly) {
      return false;
    }
    if (this.losAllowDecimal) {
      return true;
    }
    return input.type === 'number';
  }

  private sanitize(): void {
    const input = this.element.nativeElement;
    if (input.disabled || input.readOnly) {
      return;
    }

    const sanitized = this.sanitizeValue(input.value);
    if (sanitized === input.value) {
      return;
    }

    const start = input.selectionStart ?? sanitized.length;
    const end = input.selectionEnd ?? sanitized.length;
    input.value = sanitized;
    input.setSelectionRange(start, end);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private sanitizeValue(value: string): string {
    if (this.allowDecimal()) {
      let result = '';
      let hasDot = false;
      for (const char of value) {
        if (char >= '0' && char <= '9') {
          result += char;
        } else if (char === '.' && !hasDot) {
          hasDot = true;
          result += char;
        }
      }
      return result;
    }

    return value.replace(/\D/g, '');
  }
}
